import React, { useEffect, useContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { AdminContext } from 'constants/contexts';
import http from 'utils/http';
import {
  Card, Typography, Spin, Alert, Result, Button,
} from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { GmailIcon } from 'components/icons/EmailBrands';
import withGuard from 'components/withGuard';
import { PERMISSIONS } from 'constants/permissions';
import { RedirectCard } from './RedirectCard';

export function GmailConnect() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if this is an error redirect return
    const urlParams = new URLSearchParams(location.search);
    const errorParam = urlParams.get('error');

    if (errorParam) {
      setError('Failed to connect with Gmail. Please try again later.');
      return;
    }

    async function startOAuth() {
      try {
        const res = await http.get('/v1/integrations/gmail/oauth/start/');
        window.location.href = res.data.auth_url;
      } catch (err) {
        // Instead of redirecting (which could cause a loop),
        // set an error state to display to the user
        setError('Unable to start Gmail authentication. Please try again later.');
      }
    }

    startOAuth();
  }, [navigate, location.search]);

  if (error) {
    return (
      <Card
        style={{
          maxWidth: '480px',
          width: '100%',
          margin: '80px auto',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        }}
      >
        <Result
          status="error"
          title="Connection Failed"
          subTitle={error}
          extra={[
            <Button type="primary" key="back" onClick={() => navigate('/console/setting')}>
              Back to Settings
            </Button>,
          ]}
        />
      </Card>
    );
  }

  return <RedirectCard title="Redirecting to Google..." description="Please wait while we redirect you to Google..." />;
}

export function GmailComplete() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Processing your request...');

  useEffect(() => {
    async function handleCallback() {
      const urlParams = new URLSearchParams(location.search);
      const state = urlParams.get('state');
      const code = urlParams.get('code');

      if (!state || !code) {
        setStatus('error');
        setMessage('Error: Missing required parameters');
        return;
      }

      try {
        const response = await http.get(`/v1/integrations/gmail/oauth/callback/?state=${state}&code=${code}&organization_id={{organizationId}}`);
        const isResponseOk = response.status >= 200 && response.status < 300;

        if (isResponseOk) {
          setStatus('success');
          setMessage('Success! Gmail authentication completed.');

          setTimeout(() => {
            navigate('/console/setting');
          }, 2000);
        } else {
          const errorData = response.data || '';
          setStatus('error');
          setMessage(`Error: ${errorData || 'Failed to complete Gmail authentication'}`);
        }
      } catch (error) {
        setStatus('error');
        setMessage(`Error: ${error.message || 'Something went wrong'}`);
      }
    }

    handleCallback();
  }, []);

  const getStatusBackgroundColor = () => {
    if (status === 'success') return '#e6f7ff';
    if (status === 'error') return '#fff2f0';
    return '#f0f5ff';
  };

  return (
    <div className="flex justify-center items-center p-6">
      <Card
        style={{
          maxWidth: '480px',
          width: '100%',
          margin: '80px auto',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          textAlign: 'center',
        }}
      >
        <GmailIcon style={{ fontSize: 40, color: '#1890ff', marginBottom: '1.5rem' }} />

        <Typography.Title level={2} style={{ marginBottom: '0.5rem' }}>
          Gmail Authorization
        </Typography.Title>

        <Typography.Paragraph>
          Completing Gmail account integration...
        </Typography.Paragraph>

        <div style={{
          padding: '1rem',
          borderRadius: '8px',
          margin: '1.5rem 0',
          backgroundColor: getStatusBackgroundColor(),
        }}
        >
          {status === 'loading' && <Spin style={{ margin: '0 auto 0.5rem' }} />}

          {status === 'success' && (
            <CheckCircleFilled style={{ fontSize: 24, color: '#52c41a', marginBottom: '0.5rem' }} />
          )}

          <Typography.Paragraph style={{ margin: 0 }}>
            {message}
          </Typography.Paragraph>
        </div>

        <Alert
          message="Beta Feature"
          description="This feature is still in beta testing. The final product may vary."
          type="warning"
          showIcon
          style={{ textAlign: 'left', marginTop: '1.5rem' }}
        />
      </Card>
    </div>
  );
}

export default withGuard(GmailConnect, [PERMISSIONS.ADMIN_ACCESS], {
  redirect: '/login',
});
